import type { ComponentProps } from "react";

import { cn } from "../foundations/cn.js";
import { FIELD_FOCUS, FIELD_WELL } from "../foundations/interaction.js";
import { Field } from "./Field.js";
import { FIELD_TEXT } from "./Input.styles.js";

export interface TextareaProps extends Omit<ComponentProps<"textarea">, "className" | "rows"> {
  /** Shown above the field and used as its name. Always required, even when hidden. */
  label: string;
  hideLabel?: boolean;
  /** One line under the field: what the text is for, its limits. */
  helper?: string;
  /** What's wrong, in words. Replaces the helper and marks the field invalid. */
  error?: string;
  invalid?: boolean;
  /** The height it starts at, in lines. */
  rows?: number;
  /** The most lines it grows to before it scrolls. */
  maxRows?: number;
  /** Shows "used / maxLength" on the label's line. Needs `maxLength`. */
  showCount?: boolean;
  className?: string;
}

/**
 * Several lines of text: a description, a message, a note. It grows with its text up to
 * `maxRows`, then scrolls.
 */
export function Textarea({
  label,
  hideLabel,
  helper,
  error,
  invalid,
  rows = 3,
  maxRows = 8,
  showCount = false,
  maxLength,
  value,
  defaultValue,
  id,
  className,
  ...props
}: TextareaProps) {
  const used = String(value ?? defaultValue ?? "").length;
  const count =
    showCount && maxLength !== undefined ? (
      <span className="font-mono text-mono-meta text-text-tertiary tabular-nums">
        {used}/{maxLength}
        <span className="sr-only"> characters</span>
      </span>
    ) : undefined;
  return (
    <Field
      label={label}
      hideLabel={hideLabel}
      helper={helper}
      error={error}
      invalid={invalid}
      labelEnd={count}
      id={id}
      className={className}
    >
      {(field) => (
        <textarea
          id={field.id}
          rows={rows}
          maxLength={maxLength}
          value={value}
          defaultValue={defaultValue}
          aria-invalid={field.invalid || undefined}
          aria-describedby={field.describedBy}
          // Grows with its text where the browser can size a field to its content; elsewhere it
          // keeps `rows` and scrolls.
          style={{
            minBlockSize: `calc(${rows}lh + 1.25rem)`,
            maxBlockSize: `calc(${maxRows}lh + 1.25rem)`,
          }}
          className={cn(
            "w-full resize-none px-3.5 py-2.5 font-body field-sizing-content",
            FIELD_WELL,
            FIELD_FOCUS,
            FIELD_TEXT,
            field.invalid && "border-status-danger focus-within:border-status-danger",
          )}
          {...props}
        />
      )}
    </Field>
  );
}
